import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import DomainIcon from '@mui/icons-material/Domain';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import ApiIcon from '@mui/icons-material/Api';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import ErrorNotification from '../../components/ErrorNotification';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';

const brandColor = PRIMARYCOLOR || '#005CC5';

const scoreColumns = [
  { key: 'risk_score_all', label: 'Overall', color: brandColor, icon: AnalyticsOutlinedIcon },
  { key: 'risk_score_external', label: 'External', color: '#16a34a', icon: PublicIcon },
  { key: 'risk_score_web', label: 'Web', color: '#2563eb', icon: LanguageIcon },
  { key: 'risk_score_api', label: 'API', color: '#0ea5e9', icon: ApiIcon },
  { key: 'risk_score_vishing', label: 'Vishing', color: '#f59e0b', icon: PhoneIcon },
  { key: 'risk_score_darkweb', label: 'Darkweb', color: '#7c3aed', icon: LockIcon },
];

const getGrade = (score) => {
  if (score === null || score === undefined || Number(score) < 0) return null;
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

const getGradeColor = (grade) => {
  switch (grade) {
    case 'A+':
    case 'A-':
      return '#16a34a';
    case 'B':
      return '#2563eb';
    case 'C':
      return '#f59e0b';
    case 'D':
      return '#eab308';
    case 'F':
      return '#ef4444';
    default:
      return '#94a3b8';
  }
};

const hasScore = (score) => score !== null && score !== undefined && Number(score) >= 0;

const clampScore = (score) => (hasScore(score) ? Math.max(0, Math.min(100, Number(score))) : 0);

const getHealthInfo = (score) => {
  if (!hasScore(score)) {
    return {
      key: 'unscored',
      label: 'No Grade Yet',
      color: '#64748b',
      soft: alpha('#64748b', 0.1),
      border: alpha('#64748b', 0.18),
      icon: AccessTimeIcon,
    };
  }

  if (score < 80) {
    return {
      key: 'attention',
      label: 'Needs Attention',
      color: '#dc2626',
      soft: alpha('#dc2626', 0.1),
      border: alpha('#dc2626', 0.18),
      icon: WarningAmberIcon,
    };
  }

  if (score >= 90) {
    return {
      key: 'strong',
      label: 'Strong Posture',
      color: '#16a34a',
      soft: alpha('#16a34a', 0.1),
      border: alpha('#16a34a', 0.18),
      icon: CheckCircleOutlineIcon,
    };
  }

  return {
    key: 'stable',
    label: 'Stable Posture',
    color: '#2563eb',
    soft: alpha('#2563eb', 0.1),
    border: alpha('#2563eb', 0.18),
    icon: AnalyticsOutlinedIcon,
  };
};

const compareScores = (a, b, direction = 'asc') => {
  const aHas = hasScore(a);
  const bHas = hasScore(b);

  if (!aHas && !bHas) return 0;
  if (!aHas) return 1;
  if (!bHas) return -1;

  const av = Number(a);
  const bv = Number(b);

  if (av < bv) return direction === 'asc' ? -1 : 1;
  if (av > bv) return direction === 'asc' ? 1 : -1;
  return 0;
};

const SummaryCard = ({ title, value, helper, accent, icon }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 4,
      border: `1px solid ${alpha(accent, 0.14)}`,
      background: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, rgba(255,255,255,0.98) 60%)`,
      boxShadow: `0 16px 34px ${alpha(accent, 0.08)}`,
    }}
  >
    <CardContent sx={{ p: 2.2 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58) }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.8, fontSize: '1.95rem', lineHeight: 1, fontWeight: 900, color: '#0f172a' }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ width: 42, height: 42, bgcolor: alpha(accent, 0.14), color: accent, border: `1px solid ${alpha(accent, 0.18)}` }}>
          {icon}
        </Avatar>
      </Stack>
      <Typography sx={{ mt: 1.25, fontSize: '0.88rem', lineHeight: 1.55, color: alpha('#0f172a', 0.72) }}>
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const HealthChip = ({ health }) => {
  const Icon = health.icon;
  return (
    <Chip
      icon={<Icon fontSize="small" />}
      label={health.label}
      size="small"
      sx={{
        bgcolor: health.soft,
        color: health.color,
        border: `1px solid ${health.border}`,
        fontWeight: 800,
        borderRadius: 999,
        '& .MuiChip-icon': { color: health.color },
      }}
    />
  );
};

const ScoreChip = ({ score, label, color, icon: Icon, subtle = false }) => {
  const grade = getGrade(score);

  if (!grade) {
    return (
      <Chip
        icon={Icon ? <Icon fontSize="small" /> : undefined}
        label={label ? `${label}: No grade` : 'No grade'}
        size="small"
        sx={{
          bgcolor: subtle ? alpha('#64748b', 0.08) : alpha('#64748b', 0.1),
          color: '#64748b',
          border: `1px solid ${alpha('#64748b', 0.15)}`,
          fontWeight: 700,
          borderRadius: 999,
          '& .MuiChip-icon': { color: '#64748b' },
        }}
      />
    );
  }

  const gradeColor = color || getGradeColor(grade);
  const scoreLabel = `${grade} ${Math.round(Number(score))}/100`;

  return (
    <Chip
      icon={Icon ? <Icon fontSize="small" /> : undefined}
      label={label ? `${label}: ${scoreLabel}` : scoreLabel}
      size="small"
      sx={{
        bgcolor: alpha(gradeColor, subtle ? 0.08 : 0.12),
        color: gradeColor,
        border: `1px solid ${alpha(gradeColor, 0.16)}`,
        fontWeight: 800,
        borderRadius: 999,
        '& .MuiChip-icon': { color: gradeColor },
      }}
    />
  );
};

const OverallScoreDisplay = ({ score }) => {
  const grade = getGrade(score);
  const value = clampScore(score);

  if (!grade) {
    return (
      <Chip
        label="Pending"
        size="small"
        sx={{
          bgcolor: alpha('#64748b', 0.1),
          color: '#64748b',
          border: `1px solid ${alpha('#64748b', 0.18)}`,
          fontWeight: 800,
          borderRadius: 999,
        }}
      />
    );
  }

  const gradeColor = getGradeColor(grade);

  return (
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress variant="determinate" value={value} size={42} thickness={5} sx={{ color: gradeColor }} />
        <Box sx={{ inset: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: gradeColor, color: '#ffffff', fontWeight: 900 }}>
            {grade}
          </Avatar>
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a' }}>{Math.round(Number(score))} / 100</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: alpha('#0f172a', 0.55) }}>Overall grade</Typography>
      </Box>
    </Stack>
  );
};

const OrganizationPreviewDrawer = ({
  open,
  organization,
  onClose,
  onOpenDashboard,
  onOpenPentests,
  onDelete,
}) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: { xs: '100%', sm: 540 },
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8fbff',
      },
    }}
  >
    {organization ? (
      <>
        <Box
          sx={{
            px: 2.5,
            py: 2.25,
            background: `linear-gradient(135deg, ${brandColor} 0%, ${darken(brandColor, 0.2)} 100%)`,
            color: '#ffffff',
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack spacing={1.25} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <HealthChip health={organization.health} />
                <Chip
                  icon={<DomainIcon fontSize="small" />}
                  label={`${organization.pentest_count} pentests`}
                  size="small"
                  sx={{
                    bgcolor: alpha('#ffffff', 0.12),
                    color: '#ffffff',
                    border: `1px solid ${alpha('#ffffff', 0.22)}`,
                    fontWeight: 800,
                    '& .MuiChip-icon': { color: '#ffffff' },
                  }}
                />
              </Stack>
              <Typography sx={{ fontSize: '1.65rem', fontWeight: 900, lineHeight: 1.1 }}>
                {organization.name}
              </Typography>
              <Typography sx={{ fontSize: '0.92rem', lineHeight: 1.6, color: alpha('#ffffff', 0.92) }}>
                {organization.description}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} sx={{ color: '#ffffff', alignSelf: 'flex-start' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ p: 2.25, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Card elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58) }}>
                  Pentests
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: '1.4rem', fontWeight: 900, color: '#0f172a' }}>
                  {organization.pentest_count}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58) }}>
                  Surfaces Graded
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: '1.4rem', fontWeight: 900, color: '#0f172a' }}>
                  {organization.coverageCount}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58) }}>
                  Overall Posture
                </Typography>
                <Box sx={{ mt: 0.85 }}>
                  <OverallScoreDisplay score={organization.risk_score_all} />
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)', mb: 2 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58), mb: 1.25 }}>
              Assessment Coverage
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {scoreColumns.map((column) => (
                <ScoreChip
                  key={column.key}
                  score={organization[column.key]}
                  label={column.label}
                  color={column.color}
                  icon={column.icon}
                  subtle
                />
              ))}
            </Stack>
          </Card>

          <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)' }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58), mb: 1.25 }}>
              Quick Signals
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label={`Health: ${organization.health.label}`} sx={{ borderRadius: 999 }} />
              <Chip label={`Overall: ${getGrade(organization.risk_score_all) || 'Pending'}`} sx={{ borderRadius: 999 }} />
              <Chip label={`Pentests: ${organization.pentest_count}`} sx={{ borderRadius: 999 }} />
            </Stack>
          </Card>
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid rgba(15,23,42,0.08)', bgcolor: '#ffffff' }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={onOpenDashboard} sx={{ flex: 1, textTransform: 'none', fontWeight: 800, borderRadius: 2.5 }}>
                Open Dashboard
              </Button>
              <Button variant="outlined" onClick={onOpenPentests} sx={{ textTransform: 'none', borderRadius: 2.5 }}>
                View Pentests
              </Button>
            </Stack>
            <Button variant="outlined" color="error" onClick={onDelete} sx={{ textTransform: 'none', borderRadius: 2.5 }}>
              Delete Organization
            </Button>
          </Stack>
        </Box>
      </>
    ) : null}
  </Drawer>
);

const OrganizationGridCard = ({ row, onOpen, onPreview, onDelete }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 4,
      border: `1px solid ${row.health.key === 'attention' ? alpha('#ef4444', 0.16) : alpha('#0f172a', 0.08)}`,
      boxShadow: row.health.key === 'attention' ? `0 18px 34px ${alpha('#ef4444', 0.08)}` : '0 16px 34px rgba(15,23,42,0.06)',
      background: 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)',
    }}
  >
    <CardContent sx={{ p: 2, flex: 1 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1.2} sx={{ mb: 1.2 }}>
        <HealthChip health={row.health} />
        <Chip
          label={`${row.pentest_count} pentests`}
          size="small"
          sx={{
            bgcolor: alpha(brandColor, 0.08),
            color: brandColor,
            border: `1px solid ${alpha(brandColor, 0.14)}`,
            fontWeight: 800,
            borderRadius: 999,
          }}
        />
      </Stack>

      <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, color: '#0f172a', mb: 0.65 }}>
        {row.name}
      </Typography>
      <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.6, color: alpha('#0f172a', 0.72), mb: 1.25 }}>
        {row.description}
      </Typography>

      <Stack spacing={1.2}>
        <OverallScoreDisplay score={row.risk_score_all} />
        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
          {scoreColumns.slice(1).map((column) => (
            <ScoreChip
              key={column.key}
              score={row[column.key]}
              label={column.label}
              color={column.color}
              icon={column.icon}
              subtle
            />
          ))}
        </Stack>
      </Stack>
    </CardContent>

    <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
      <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={() => onOpen(row)} sx={{ flex: 1, textTransform: 'none', fontWeight: 800, borderRadius: 2.5 }}>
        Open
      </Button>
      <Button variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={() => onPreview(row)} sx={{ textTransform: 'none', borderRadius: 2.5 }}>
        Preview
      </Button>
      <Tooltip title="Delete organization" arrow>
        <IconButton color="error" onClick={() => onDelete(row)}>
          <DeleteOutlineIcon />
        </IconButton>
      </Tooltip>
    </CardActions>
  </Card>
);

const Organizations = () => {
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortBy, setSortBy] = useState('risk_score_all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  const getOrganizations = useCallback((token) => {
    setLoading(true);
    fetch(`${BASEURL}/organizations`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          if (data.message === 'Invalid Token') navigate('/');
          else setError(data.message || 'Error fetching organizations');
          setOrgs([]);
          return;
        }

        const list = (data.organizations || []).map((organization) => ({
          ...organization,
          pentest_count: organization.pentest_count ?? 0,
        }));

        setOrgs(list);
        setError(null);
      })
      .catch(() => setError('Error fetching organizations'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) getOrganizations(token);
    else navigate('/');
  }, [getOrganizations, navigate]);

  const organizations = useMemo(
    () =>
      (orgs || []).map((organization) => {
        const overallScore = organization.risk_score_all;
        const health = getHealthInfo(overallScore);
        const gradedScores = scoreColumns.filter((column) => hasScore(organization[column.key]));
        const overallGrade = getGrade(overallScore);

        return {
          ...organization,
          health,
          coverageCount: gradedScores.length,
          overallGrade,
          description: hasScore(overallScore)
            ? `${organization.pentest_count ?? 0} pentests tracked with an overall ${overallGrade} posture.`
            : `${organization.pentest_count ?? 0} pentests tracked with scoring still in progress.`,
        };
      }),
    [orgs]
  );

  const summary = useMemo(() => {
    const scored = organizations.filter((organization) => hasScore(organization.risk_score_all));
    const attention = organizations.filter((organization) => organization.health.key === 'attention');
    const pentestsTracked = organizations.reduce((sum, organization) => sum + Number(organization.pentest_count || 0), 0);
    const averageScore = scored.length
      ? Math.round(scored.reduce((sum, organization) => sum + Number(organization.risk_score_all || 0), 0) / scored.length)
      : null;

    return {
      total: organizations.length,
      scored: scored.length,
      attention: attention.length,
      pentestsTracked,
      averageScore,
    };
  }, [organizations]);

  const filteredOrganizations = useMemo(() => {
    const query = searchInput.trim().toLowerCase();

    return organizations.filter((organization) => {
      const matchesSearch =
        !query ||
        (organization.name || '').toLowerCase().includes(query) ||
        organization.health.label.toLowerCase().includes(query);

      const matchesHealth =
        healthFilter === 'all' ||
        (healthFilter === 'attention' && organization.health.key === 'attention') ||
        (healthFilter === 'healthy' && ['stable', 'strong'].includes(organization.health.key)) ||
        (healthFilter === 'unscored' && organization.health.key === 'unscored');

      return matchesSearch && matchesHealth;
    });
  }, [healthFilter, organizations, searchInput]);

  const sortedOrganizations = useMemo(() => {
    const list = [...filteredOrganizations];

    list.sort((a, b) => {
      if (scoreColumns.some((column) => column.key === sortBy)) {
        return compareScores(a?.[sortBy], b?.[sortBy], sortOrder);
      }

      if (sortBy === 'pentest_count') {
        const av = Number(a?.pentest_count || 0);
        const bv = Number(b?.pentest_count || 0);
        if (av < bv) return sortOrder === 'asc' ? -1 : 1;
        if (av > bv) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }

      const av = a?.[sortBy];
      const bv = b?.[sortBy];

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === 'string' && typeof bv === 'string') {
        return sortOrder === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }

      if (av < bv) return sortOrder === 'asc' ? -1 : 1;
      if (av > bv) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [filteredOrganizations, sortBy, sortOrder]);

  const paginatedOrganizations = useMemo(
    () => sortedOrganizations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, sortedOrganizations]
  );

  const showEmpty = !loading && filteredOrganizations.length === 0;

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const handleOpenPreview = (organization) => {
    setSelectedOrg(organization);
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedOrg(null);
  };

  const handleDeleteOrg = (organization) => {
    setSelectedOrg(organization);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedOrg) return;

    const token = sessionStorage.getItem('token');
    fetch(`${BASEURL}/organizations?organization_id=${selectedOrg.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setOrgs((previous) => previous.filter((organization) => organization.id !== selectedOrg.id));
          setError(null);
        } else {
          setError(data.message || 'Failed to delete organization');
        }
      })
      .catch(() => setError('Failed to delete organization'))
      .finally(() => {
        setOpenDeleteDialog(false);
        setOpenPreview(false);
        setSelectedOrg(null);
      });
  };

  const openCreateDialog = () => {
    setNewOrgName('');
    setOpenCreate(true);
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setOpenCreate(false);
    setNewOrgName('');
  };

  const handleCreateOrg = () => {
    const name = (newOrgName || '').trim();
    if (!name) {
      setError('Organization name is required');
      return;
    }

    setCreating(true);
    const token = sessionStorage.getItem('token');
    fetch(`${BASEURL}/organizations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          const newOrganization = {
            id: data.organizationId,
            name,
            team_id: null,
            pentest_count: 0,
            risk_score_all: -1,
            risk_score_external: -1,
            risk_score_web: -1,
            risk_score_api: -1,
            risk_score_vishing: -1,
            risk_score_darkweb: -1,
          };

          setOrgs((previous) => [newOrganization, ...previous]);
          setOpenCreate(false);
          setNewOrgName('');
          setError(null);
        } else {
          setError(data.message || 'Failed to create organization');
        }
      })
      .catch(() => setError('Failed to create organization'))
      .finally(() => setCreating(false));
  };

  const openOrganizationDashboard = (organization) => navigate(`/organization/dashboard/${organization.id}`);
  const openOrganizationPentests = (organization) => navigate(`/organization/pentests/${organization.id}`);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: alpha('#eaf2ff', 0.6),
        backgroundImage: `radial-gradient(${alpha('#bdd8ff', 0.45)} 1px, transparent 1px)`,
        backgroundSize: '16px 16px',
      }}
    >
      <ErrorNotification errorMessage={error} />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            mb: 2.4,
            borderRadius: 5,
            border: `1px solid ${alpha(brandColor, 0.14)}`,
            bgcolor: '#ffffff',
            boxShadow: `0 18px 42px ${alpha(brandColor, 0.08)}`,
          }}
        >
          <Box aria-hidden sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: brandColor }} />
          <Box aria-hidden sx={{ position: 'absolute', top: -90, right: -30, width: 260, height: 260, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(brandColor, 0.14)} 0%, rgba(255,255,255,0) 72%)` }} />
          <Box sx={{ position: 'relative', p: { xs: 2, md: 3 } }}>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2.5}>
              <Box sx={{ maxWidth: 760 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: brandColor }}>
                  Organizations
                </Typography>
                <Typography variant="h3" sx={{ mt: 0.8, fontWeight: 900, letterSpacing: '-0.03em', color: '#0f172a', fontSize: { xs: '2rem', md: '2.45rem' } }}>
                  Client Portfolio
                </Typography>
                <Typography sx={{ mt: 1, maxWidth: 560, fontSize: '0.98rem', lineHeight: 1.6, color: alpha('#0f172a', 0.68) }}>
                  Monitor client health, review scores, and jump into the right engagement.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.6 }}>
                  <Chip
                    label={`${summary.total} organizations`}
                    sx={{
                      bgcolor: alpha(brandColor, 0.1),
                      color: brandColor,
                      border: `1px solid ${alpha(brandColor, 0.16)}`,
                      fontWeight: 800,
                      borderRadius: 999,
                    }}
                  />
                  <Chip
                    label={`${summary.scored} scored`}
                    sx={{
                      bgcolor: alpha('#16a34a', 0.1),
                      color: '#15803d',
                      border: `1px solid ${alpha('#16a34a', 0.16)}`,
                      fontWeight: 800,
                      borderRadius: 999,
                    }}
                  />
                  <Chip
                    label={summary.averageScore !== null ? `Avg score ${summary.averageScore}/100` : 'No grades yet'}
                    sx={{
                      bgcolor: alpha('#0f172a', 0.05),
                      color: '#0f172a',
                      border: `1px solid ${alpha('#0f172a', 0.08)}`,
                      fontWeight: 800,
                      borderRadius: 999,
                    }}
                  />
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  onClick={openCreateDialog}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    borderRadius: 2.5,
                    px: 2.6,
                    py: 1.2,
                    boxShadow: `0 14px 28px ${alpha(brandColor, 0.2)}`,
                  }}
                >
                  New Organization
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} xl={3}>
                <SummaryCard
                  title="Organizations"
                  value={summary.total}
                  helper="Every client account currently tracked in the platform."
                  accent={brandColor}
                  icon={<DomainIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3}>
                <SummaryCard
                  title="Scored Clients"
                  value={summary.scored}
                  helper="Organizations with enough completed testing to show an overall grade."
                  accent="#16a34a"
                  icon={<CheckCircleOutlineIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3}>
                <SummaryCard
                  title="Needs Attention"
                  value={summary.attention}
                  helper="Client environments with an overall score below 80 that deserve a closer look."
                  accent="#dc2626"
                  icon={<WarningAmberIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3}>
                <SummaryCard
                  title="Pentests Tracked"
                  value={summary.pentestsTracked}
                  helper="Combined number of pentests attached across all organizations."
                  accent="#7c3aed"
                  icon={<AnalyticsOutlinedIcon />}
                />
              </Grid>
            </Grid>
          </Stack>
          </Box>
        </Paper>

        <Card
          elevation={0}
          sx={{
            p: 2.25,
            borderRadius: 4,
            mb: 3,
            border: `1px solid ${alpha('#0f172a', 0.06)}`,
            boxShadow: '0 16px 34px rgba(15,23,42,0.05)',
          }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <TextField
                  fullWidth
                  placeholder="Search clients by name or health status..."
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: searchInput ? (
                      <IconButton size="small" onClick={() => setSearchInput('')}>
                        <ClearIcon />
                      </IconButton>
                    ) : null,
                  }}
                />
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <ToggleButtonGroup
                  exclusive
                  value={healthFilter}
                  onChange={(_, value) => value && setHealthFilter(value)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 1.6,
                    },
                  }}
                >
                  <ToggleButton value="all">All Clients</ToggleButton>
                  <ToggleButton value="attention">Needs Attention</ToggleButton>
                  <ToggleButton value="healthy">Healthy</ToggleButton>
                  <ToggleButton value="unscored">No Grade</ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                  exclusive
                  value={viewMode}
                  onChange={(_, value) => value && setViewMode(value)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 1.3,
                    },
                  }}
                >
                  <ToggleButton value="table">
                    <TableRowsIcon sx={{ mr: 0.8, fontSize: 18 }} />
                    Table
                  </ToggleButton>
                  <ToggleButton value="grid">
                    <GridViewIcon sx={{ mr: 0.8, fontSize: 18 }} />
                    Cards
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                label="Alphabetical"
                onClick={() => {
                  setSortBy('name');
                  setSortOrder('asc');
                }}
                clickable
                icon={<DomainIcon fontSize="small" />}
                sx={{
                  borderRadius: 999,
                  fontWeight: 700,
                  bgcolor: sortBy === 'name' ? alpha(brandColor, 0.1) : '#ffffff',
                  color: sortBy === 'name' ? brandColor : alpha('#0f172a', 0.78),
                  border: `1px solid ${sortBy === 'name' ? alpha(brandColor, 0.2) : alpha('#0f172a', 0.08)}`,
                }}
              />
              <Chip
                label="Highest Risk"
                onClick={() => {
                  setSortBy('risk_score_all');
                  setSortOrder('asc');
                }}
                clickable
                icon={<WarningAmberIcon fontSize="small" />}
                sx={{
                  borderRadius: 999,
                  fontWeight: 700,
                  bgcolor: sortBy === 'risk_score_all' && sortOrder === 'asc' ? alpha('#dc2626', 0.08) : '#ffffff',
                  color: sortBy === 'risk_score_all' && sortOrder === 'asc' ? '#dc2626' : alpha('#0f172a', 0.78),
                  border: `1px solid ${sortBy === 'risk_score_all' && sortOrder === 'asc' ? alpha('#dc2626', 0.18) : alpha('#0f172a', 0.08)}`,
                }}
              />
              <Chip
                label="Most Pentests"
                onClick={() => {
                  setSortBy('pentest_count');
                  setSortOrder('desc');
                }}
                clickable
                icon={<AnalyticsOutlinedIcon fontSize="small" />}
                sx={{
                  borderRadius: 999,
                  fontWeight: 700,
                  bgcolor: sortBy === 'pentest_count' ? alpha('#7c3aed', 0.08) : '#ffffff',
                  color: sortBy === 'pentest_count' ? '#7c3aed' : alpha('#0f172a', 0.78),
                  border: `1px solid ${sortBy === 'pentest_count' ? alpha('#7c3aed', 0.18) : alpha('#0f172a', 0.08)}`,
                }}
              />
            </Stack>
          </Stack>
        </Card>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: `1px solid ${alpha('#0f172a', 0.06)}`,
            boxShadow: '0 18px 38px rgba(15,23,42,0.05)',
          }}
        >
          {loading ? (
            <Box sx={{ p: 3 }}>
              {[...Array(6)].map((_, index) => (
                <Skeleton key={index} variant="rounded" height={62} sx={{ mb: 1.2 }} />
              ))}
            </Box>
          ) : showEmpty ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 2,
                m: 3,
                borderRadius: 4,
                border: `2px dashed ${alpha('#94a3b8', 0.3)}`,
                bgcolor: alpha('#f8fafc', 0.9),
              }}
            >
              <DomainIcon sx={{ fontSize: 56, color: brandColor, mb: 1 }} />
              <Typography sx={{ fontSize: '1.45rem', fontWeight: 900, mb: 0.8, color: '#0f172a' }}>
                No Organizations Found
              </Typography>
              <Typography sx={{ mb: 2, color: alpha('#0f172a', 0.68) }}>
                Create your first client or loosen the current filters to see the portfolio again.
              </Typography>
              <Button variant="contained" onClick={openCreateDialog} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 800 }}>
                Create Organization
              </Button>
            </Box>
          ) : viewMode === 'grid' ? (
            <Box sx={{ p: 2.2 }}>
              <Grid container spacing={2}>
                {paginatedOrganizations.map((row) => (
                  <Grid item xs={12} md={6} xl={4} key={row.id || row.name}>
                    <OrganizationGridCard
                      row={row}
                      onOpen={openOrganizationDashboard}
                      onPreview={handleOpenPreview}
                      onDelete={handleDeleteOrg}
                    />
                  </Grid>
                ))}
              </Grid>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={sortedOrganizations.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                sx={{ mt: 1.5, '& .MuiTablePagination-toolbar': { px: 0 } }}
              />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          background: 'linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)',
                          color: '#0f172a',
                          fontWeight: 800,
                          borderBottom: `1px solid ${alpha(brandColor, 0.12)}`,
                        },
                      }}
                    >
                      <TableCell sortDirection={sortBy === 'name' ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === 'name'}
                          direction={sortBy === 'name' ? sortOrder : 'asc'}
                          onClick={() => handleSort('name')}
                        >
                          Organization
                        </TableSortLabel>
                      </TableCell>
                      {scoreColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          sortDirection={sortBy === column.key ? sortOrder : false}
                          width={170}
                        >
                          <TableSortLabel
                            active={sortBy === column.key}
                            direction={sortBy === column.key ? sortOrder : 'asc'}
                            onClick={() => handleSort(column.key)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell align="right" sortDirection={sortBy === 'pentest_count' ? sortOrder : false} width={130}>
                        <TableSortLabel
                          active={sortBy === 'pentest_count'}
                          direction={sortBy === 'pentest_count' ? sortOrder : 'desc'}
                          onClick={() => handleSort('pentest_count')}
                        >
                          Pentests
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" width={170}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody
                    sx={{
                      '& tr:nth-of-type(odd)': { backgroundColor: alpha('#f8fafc', 0.95) },
                      '& tr:hover': { backgroundColor: alpha(brandColor, 0.05) },
                    }}
                  >
                    {paginatedOrganizations.map((row) => (
                      <TableRow key={row.id || row.name} hover>
                        <TableCell sx={{ minWidth: 300 }}>
                          <Stack spacing={0.75}>
                            <Box component="button" type="button" onClick={() => handleOpenPreview(row)} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}>
                              <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{row.name}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.55, color: alpha('#0f172a', 0.66) }}>
                              {row.description}
                            </Typography>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              <HealthChip health={row.health} />
                            </Stack>
                          </Stack>
                        </TableCell>

                        {scoreColumns.map((column) => (
                          <TableCell key={column.key}>
                            {column.key === 'risk_score_all' ? (
                              <OverallScoreDisplay score={row[column.key]} />
                            ) : (
                              <ScoreChip score={row[column.key]} color={column.color} icon={column.icon} />
                            )}
                          </TableCell>
                        ))}

                        <TableCell align="right">
                          <Chip
                            label={row.pentest_count}
                            size="small"
                            sx={{
                              fontWeight: 800,
                              borderRadius: 999,
                              bgcolor: alpha('#7c3aed', 0.08),
                              color: '#7c3aed',
                              border: `1px solid ${alpha('#7c3aed', 0.14)}`,
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Stack direction="row" spacing={0.75} justifyContent="center">
                            <Tooltip title="Open organization" arrow>
                              <IconButton color="primary" onClick={() => openOrganizationDashboard(row)}>
                                <OpenInNewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Preview organization" arrow>
                              <IconButton color="info" onClick={() => handleOpenPreview(row)}>
                                <VisibilityOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete organization" arrow>
                              <IconButton color="error" onClick={() => handleDeleteOrg(row)}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={sortedOrganizations.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                sx={{ px: 2, '& .MuiTablePagination-toolbar': { py: 1.2 } }}
              />
            </>
          )}
        </Card>
      </Box>

      <OrganizationPreviewDrawer
        open={openPreview}
        organization={selectedOrg}
        onClose={handleClosePreview}
        onOpenDashboard={() => {
          if (selectedOrg) {
            handleClosePreview();
            openOrganizationDashboard(selectedOrg);
          }
        }}
        onOpenPentests={() => {
          if (selectedOrg) {
            handleClosePreview();
            openOrganizationPentests(selectedOrg);
          }
        }}
        onDelete={() => setOpenDeleteDialog(true)}
      />

      <Dialog open={openCreate} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${brandColor}, ${darken(brandColor, 0.25)})`,
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: alpha('#ffffff', 0.18) }}>
                <DomainIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, lineHeight: 1.1 }}>
                  New Organization
                </Typography>
                <Typography sx={{ mt: 0.35, fontSize: '0.84rem', color: alpha('#ffffff', 0.82) }}>
                  Create a new client account so pentests can be grouped and tracked under one portfolio.
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={closeCreateDialog} sx={{ color: '#ffffff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            label="Organization Name"
            placeholder="Acme Corporation"
            fullWidth
            value={newOrgName}
            onChange={(event) => setNewOrgName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleCreateOrg();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeCreateDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={creating} onClick={handleCreateOrg} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {creating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete <strong>{selectedOrg?.name ?? 'this organization'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Organizations;
